export const scrollTo = (container: Element, target: Element, options?: ScrollToOptions) => {
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const containerTop = containerRect.top;
  const containerLeft = containerRect.left;
  const containerScrollTop = container.scrollTop;
  const containerScrollLeft = container.scrollLeft;
  const targetTop = targetRect.top;
  const targetLeft = targetRect.left;
  const newTop = containerScrollTop + (targetTop - containerTop);
  const newLeft = containerScrollLeft + (targetLeft - containerLeft);

  container.scrollTo({ ...options, top: newTop, left: newLeft, behavior: options?.behavior || 'smooth' });
};

export const scrollIntoView = (target: Element, options?: ScrollToOptions) => {
  target.scrollIntoView(options);
};

type ScrollCompleteOptions = {
  tolerance?: number;
  timeout?: number;
  onComplete?: () => void;
};

export const waitForScrollComplete = (
  container: Element,
  expectedTop: number,
  expectedLeft: number,
  { tolerance = 0, timeout = 3000 }: ScrollCompleteOptions = {},
) =>
  new Promise<void>((resolve) => {
    let rafId = 0;
    let timeoutId: number | null = null;

    const cleanup = () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      container.removeEventListener('scroll', onScroll);
      // Some browsers fire scrollend; we prefer a cheap listener to short-circuit when available.
      // @ts-ignore: scrollend is still experimental on some DOM typings.
      container.removeEventListener('scrollend', onScrollEnd);
    };

    const finish = () => {
      cleanup();
      resolve();
    };

    const withinTolerance = () =>
      Math.abs(container.scrollTop - expectedTop) <= tolerance &&
      Math.abs(container.scrollLeft - expectedLeft) <= tolerance;

    const checkSettled = () => {
      if (withinTolerance()) {
        finish();
      } else {
        rafId = requestAnimationFrame(checkSettled);
      }
    };

    const onScroll = () => {
      rafId = requestAnimationFrame(checkSettled);
    };

    const onScrollEnd = () => {
      finish();
    };

    rafId = requestAnimationFrame(checkSettled);
    container.addEventListener('scroll', onScroll, { passive: true });
    // @ts-ignore: scrollend is still experimental on some DOM typings.
    container.addEventListener('scrollend', onScrollEnd, { once: true });
    timeoutId = window.setTimeout(finish, timeout);
  });

export const smoothScrollTo = async (container: Element, target: Element, options?: ScrollCompleteOptions) => {
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const containerTop = containerRect.top;
  const containerLeft = containerRect.left;
  const containerScrollTop = container.scrollTop;
  const containerScrollLeft = container.scrollLeft;
  const targetTop = targetRect.top;
  const targetLeft = targetRect.left;
  const newTop = containerScrollTop + (targetTop - containerTop);
  const newLeft = containerScrollLeft + (targetLeft - containerLeft);

  container.scrollTo({ top: newTop, left: newLeft, behavior: 'smooth' });

  await waitForScrollComplete(container, newTop, newLeft);
  options?.onComplete?.();
};

export const smoothScrollIntoView = async (container: Element, target: Element, options?: ScrollCompleteOptions) => {
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const containerTop = containerRect.top;
  const containerLeft = containerRect.left;
  const containerScrollTop = container.scrollTop;
  const containerScrollLeft = container.scrollLeft;
  const targetTop = targetRect.top;
  const targetLeft = targetRect.left;
  const newTop = containerScrollTop + (targetTop - containerTop);
  const newLeft = containerScrollLeft + (targetLeft - containerLeft);

  target.scrollIntoView({ behavior: 'smooth' });

  await waitForScrollComplete(container, newTop, newLeft);
  options?.onComplete?.();
};
